import type { HTMLAttributes } from "react";
import { Link } from "react-router";
import { t } from "ttag";

import { useTranslateContent } from "metabase/i18n/hooks";
import { getParentEntity } from "metabase/query_builder/selectors";
import { useDispatch, useSelector } from "metabase/redux";
import { navigateBackToDashboard } from "metabase/redux/query-builder";
import { ActionIcon, type ActionIconProps, Icon, Tooltip } from "metabase/ui";
import * as Urls from "metabase/urls";
import type { CollectionItemModel, DashboardId } from "metabase-types/api";

import S from "./QueryBuilderBackButton.module.css";

export type QueryBuilderBackButtonProps = {
  noLink?: boolean;
  onClick?: () => void;
  parentOverride?: {
    id: DashboardId | number;
    model: CollectionItemModel;
    name: string;
  };
} & ActionIconProps &
  HTMLAttributes<HTMLButtonElement>;

export function QueryBuilderBackButton({
  noLink,
  onClick,
  parentOverride,
  ...actionIconProps
}: QueryBuilderBackButtonProps) {
  const stateParent = useSelector(getParentEntity);
  const parent = parentOverride ?? stateParent;
  const dispatch = useDispatch();
  const tc = useTranslateContent();

  const handleClick = () => {
    if (parent.model === "dashboard") {
      dispatch(navigateBackToDashboard(parent.id));
    }
    onClick?.();
  };

  if (!parent.model) {
    return null;
  }

  const url = Urls.modelToUrl(parent as Urls.UrlableModel);
  if (!url) {
    return null;
  }

  const label = t`Back to ${tc(parent.name)}`;

  return (
    <Tooltip label={label}>
      <ActionIcon
        className={S.QueryBuilderBackButton}
        variant="outline"
        radius="xl"
        size="2.625rem"
        color="border-neutral"
        aria-label={label}
        onClick={handleClick}
        component={noLink ? undefined : Link}
        to={url}
        {...actionIconProps}
      >
        <Icon c="core-brand" name="arrow_left" />
      </ActionIcon>
    </Tooltip>
  );
}
